import { Component, OnInit, Input, Inject, ElementRef, OnDestroy } from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { Subject } from 'rxjs';
import { distinctUntilChanged, filter, takeUntil } from 'rxjs/operators';
import get from 'lodash-es/get';
import cloneDeep from 'lodash-es/cloneDeep';

import { WindowRef } from '@finder/shared/services/window-ref/window-ref.service';
import { CampaignDrawerService } from '@finder/shared/services/campaign-drawer/campaign-drawer.service';
import { CampaignDrawerOpen, DEFAULT_DRAWER_STATE } from '@finder/shared/services/campaign-drawer/campaign-drawer.interface';
import { BREAKPOINTS } from '@finder/shared/services/viewport-detection/viewport-detection.service';
import { HashTriggerableService } from '@finder/shared/features/hash-triggerable/hash-triggerable.service';
import { KEYBOARD } from '@finder/shared/constants/user-input.constants';
import { ViewportDetectionService } from '@finder/shared/services/viewport-detection/viewport-detection.service';
import { SafeHtmlPipe } from '@finder/shared/utils/pipes/safe-html/safe-html.pipe';
import { DisclaimerComponent } from '../disclaimer/disclaimer.component';
import { FontIconComponent } from '../font-icon/font-icon.component';

const NO_SCROLL = 'noscroll';
const MODAL_OPEN = 'modal-open';

@Component({
    selector: 'finder-campaign-drawer-detail',
    templateUrl: './campaign-drawer-detail.component.html',
    styleUrls: ['./campaign-drawer-detail.component.scss'],
    standalone: true,
    imports: [
        CommonModule,
        SafeHtmlPipe,
        DisclaimerComponent,
        FontIconComponent,
    ]
})
export class CampaignDrawerDetailComponent implements OnInit, OnDestroy {
    @Input() groupId: number;
    @Input() allowReorder = false;

    containerCss: string;
    drawerTitleId: string;
    id: string;
    cardIndex: number;
    show: boolean;
    enableTop = false;
    detailData: any = {};

    protected ariaLabelClose: string;

    private lastFocus;
    private captureFocus = false;
    private destroy$ = new Subject<void>();
    private sourceId: string;

    constructor(
        @Inject(DOCUMENT) private document: Document,
        private elementRef: ElementRef,
        private windowRef: WindowRef,
        private drawerService: CampaignDrawerService,
        private hashTriggerableService: HashTriggerableService,
        private viewportService: ViewportDetectionService
    ) {}

    ngOnInit() {
        this.show = false;
        this.drawerService.open
            .pipe(
                filter(data => data !== null), // Skip the intial one
                takeUntil(this.destroy$)
            )
            .subscribe(data => this.onOpen(data));

        if (this.allowReorder) {
            /**
             * If the detail card is allowed to move around
             * we need to make sure it goes back to the right place
             * on desktop
             */
            this.viewportService.onViewportResize()
                .pipe(
                    takeUntil(this.destroy$),
                    distinctUntilChanged()
                )
                .subscribe(() => this.adjustPosition());
        }
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
        this.removeListeners();
    }

    click(event, toTop = false) {
        event.preventDefault();

        // Broadcast the close
        this.drawerService.setOpen(DEFAULT_DRAWER_STATE);

        /* istanbul ignore if */
        if (toTop) {
            this.scrollTop();
        }

        // Cleanup - this must be _after_ the broadcast so we don't clear details
        this.hideAndCleanup(true);
    }

    /* istanbul ignore next */
    getElementRef() {
        return this.elementRef.nativeElement;
    }

    private onOpen(data: CampaignDrawerOpen) {
        if (this.groupId !== data.groupId) {
            // If the currently open group is different close this so we don't get multiple
            // open drawer groups - we have issues with the caret placement, and there is really
            // no reason why we _need_ to have multiple drawers open.
            this.hideAndCleanup();

            return;
        }

        // Check if the drawer is already opened and see if it is by another card
        if (this.show && this.cardIndex === data.cardIndex) {
            // Prevent scroll on body
            this.preventScroll();

            // Scroll the drawer group
            this.scrollIntoView();
        }

        // Get triggering element
        this.lastFocus = this.document.activeElement;
        this.sourceId = data?.sourceId;

        // Setup drawer
        this.setupDrawer(data);
        // Check to see if we need to adjust the position of the drawer card
        this.adjustPosition();
    }

    private adjustPosition() {
        // In most situations this card is not allowed to move
        if (!this.allowReorder) {
            return;
        }

        const isMobile = this.viewportService.isMobile();
        const elmRef = this.getElementRef();
        const drawerGroup = elmRef.closest('.drawer-group');

        // If there's no container just stop
        if (!drawerGroup) {
            return;
        }

        // If there's no data then stop. Tests keep tripping over this issue
        if (!this.detailData) {
            return;
        }

        /**
         * When this is used with the drawer-group we need to nest this
         * between the "finder-campaign-drawer-card" elements in mobile.
         * For desktop it should always be the last child in the container.
         */
        if (!isMobile) {
            /**
             * Move back to the last element in the container. Reach outside
             * and get the parent container for the drawer group.
             */
            drawerGroup.append(elmRef);

            return;
        }

        /**
         * We need to find the card that opened the drawer and place the detail component
         * directly under it.
         *
         * Find the drawer card it should be placed under
         */
        drawerGroup.querySelectorAll(`#card-${this.detailData.id}`).forEach(elm => {
            elm.after(elmRef);
        });
    }

    /* istanbul ignore next */
    private scrollTop() {
        this.windowRef.nativeWindow.scroll({
            top: 0,
            left: 0,
            behavior: 'smooth'
        });
    }

    /* istanbul ignore next */
    private preventScroll() {
        if (this.document.body.clientWidth < BREAKPOINTS.tablet) {
            this.document.body.classList.add(NO_SCROLL);
        }
    }

    /* istanbul ignore next */
    private scrollIntoView() {
        const groupElement = this.elementRef.nativeElement.parentElement.children[0];

        // Setup scrolling to the group container (1 level up)
        if (this.document.body.clientWidth >= BREAKPOINTS.tablet) {
            groupElement.scrollIntoView({
                block: 'start',
                behavior: 'smooth',
            });
        }
    }

    /* istanbul ignore next */
    private setupDrawer(data) {
        // Set this to be shown
        this.show = true;

        // grab data for unique IDs and class tie-ins
        const distinguisher = get(data.cardData, 'id', data.groupId);
        this.id = `drawer-detail-${distinguisher}`;
        this.drawerTitleId = `drawer-title-${distinguisher}`;

        // Bind the data incoming from the broadcasted click
        this.detailData = cloneDeep(data.cardData);
        this.cardIndex = data.cardIndex;
        this.groupId = data.groupId;
        this.enableTop = data.hasHash;
        this.ariaLabelClose = `${this.detailData?.close} ${this.detailData?.title ?? ''}`;
        // Used to set a class that can override some styles
        this.containerCss = this.elementRef.nativeElement.getAttribute('data-container');

        // Add the class to the body
        this.document.body.classList.add(MODAL_OPEN);

        // Need to wait a tick on these to ensure DOM is ready
        setTimeout(() => {
            // Add focus listeners
            this.addListeners();

            // Set focus on the 'x' button
            this.setInitialFocus();
        }, 0);
    }

    /* istanbul ignore next */
    private setInitialFocus() {
        // Set focus for keyboard nav
        const h4 = this.elementRef.nativeElement.querySelector('h4');
        if (h4) {
            h4.focus();
        }
        this.lastFocus = this.document.activeElement;
    }

    private hideAndCleanup(removeHash = false) {
        // Hide this element
        this.show = false;

        // Cleanup vm properties
        this.detailData = {};
        this.cardIndex = -1;
        this.enableTop = false;

        // Remove the 'noscroll' class
        /* istanbul ignore if */
        if (this.document.body.clientWidth < BREAKPOINTS.tablet) {
            this.document.body.classList.remove(NO_SCROLL);
        }

        this.lastFocus = false;

        this.removeListeners();

        // Remove class from body
        this.document.body.classList.remove(MODAL_OPEN);

        // Only remove the hash when the detail card is clicked
        if (removeHash) {
            this.hashTriggerableService.removeHash();
            // Once drawer is closed, we focus back to the source trigger
            this.setFocusToOrigin();
        }
    }

    /* istanbul ignore next */
    private addListeners() {
        // Listen for keys
        this.document.addEventListener('keydown', this.handleKeydown.bind(this));
        this.document.addEventListener('keyup', this.handleKeyup.bind(this));
    }

    /* istanbul ignore next */
    private removeListeners() {
        this.document.removeEventListener('keydown', this.handleKeydown.bind(this));
        this.document.removeEventListener('keyup', this.handleKeyup.bind(this));
    }

    /* istanbul ignore next */
    private setElementFocus() {
        // Get the 'x' button
        const closeButton = this.elementRef.nativeElement.querySelector('button');

        // Check if the last focus item was the button, and if it was set
        // to the "last" focusable element. In our case it is simple - we don't
        // have form fields to deal with, so it'll be an anchor
        if (closeButton === this.lastFocus) {
            // Get all the anchors
            const anchors = this.elementRef.nativeElement.querySelectorAll('a');
            if (anchors.length > 0) {
                // Set the focus on the last anchor
                anchors[anchors.length - 1].focus();
            } else {
                // Set focus to 'x' button because there are no anchors
                closeButton.focus();

                // We unfocus and refocus with a delay to allow screen reader
                // repeat the announce when focus on the same element
                setTimeout(() => closeButton.blur(), 10);
                setTimeout(() => closeButton.focus(), 25);
            }
        } else {
            // Set focus to 'x' button
            closeButton.focus();
        }
    }

    private setFocusToOrigin() {
        const sourceElement = this.document.getElementById(this.sourceId);
        sourceElement?.focus();
    }

    /**
     * The keydown listener is needed to capture the keypress so we know which key was hit.
     * This is used in conjunction with the focus handler to make sure it only bothers with
     * keys and ignores mouse or touch events - basically anything other than a TAB.
     */
    /* istanbul ignore next */
    private handleKeydown(event) {
        // Check the key and _only_ bother with trapping TAB focus.
        // If this is a click or touch then key will be undefined and
        // we won't trap. This is the SAFEST way, and may possibly be
        // missing some other key navigation that we should trap but at
        // this point it seems better to error on the side of caution.
        const keydown = event.which || event.keyCode;
        this.captureFocus = keydown === KEYBOARD.TAB_KEY_CODE;
    }

    /**
     * The keyup listener is used to check for the ESC key to trigger closing of the drawer.
     * You may wonder why not just use the down listener? Technically when you want to act on
     * key events it is better to act on the upstroke, as that is the "end" result. There
     * could be a combination of keys or whatever so down is not the best place to act.
     */
    /* istanbul ignore next */
    private handleKeyup(event) {
        const key = event.which || event.keyCode;

        if (key === KEYBOARD.ESCAPE_KEY_CODE) {
            this.click(event);
        }
    }
}
